import '@vidstack/react/player/styles/default/theme.css'
import '@vidstack/react/player/styles/default/layouts/video.css'
import './index.css'

import React, { PropsWithChildren, useRef, useEffect, useState } from 'react'
import Draggable from 'react-draggable'
import { useDispatch, useSelector } from 'react-redux'
import { selectPlayer, setMiniMode, stopVideo } from '../../store/video/playerSlice'
import { useAnalytics } from '../../utils/analytics'
import { ReportAction, useReportMutation, MediaItemDocument } from '../../graphql'
import useLiveNow from '../../utils/hooks/useLiveNow'
import {
  MediaPlayer,
  MediaProvider,
  MediaPlayerInstance,
  MediaProviderAdapter,
  isVideoProvider,
  isHLSProvider,
  MediaProviderChangeEvent,
  Captions,
} from '@vidstack/react'
import VideoLayout from './components/VideoLayout'
import config from '../../config'
import appConfig from '../../configs/app.config'

const VideoPlayer = ({ children }: PropsWithChildren) => {
  const { status, src, id, position, miniMode } = useSelector(selectPlayer)
  const [mobile, setMobile] = useState(false)
  const mobileRef = useRef(mobile)

  const dispatch = useDispatch()

  const isNativeApp = 'isNativeApp' in window && !!window.isNativeApp
  const nativeAppPlatform = isNativeApp && 'nativeAppPlatform' in window ? window.nativeAppPlatform : null
  const remoteType = isNativeApp ? (nativeAppPlatform === 'ios' ? 'iOS' : 'Android') : 'Web'

  const liveNow = useLiveNow(id)

  const [reportMutation] = useReportMutation()

  const player = useRef<MediaPlayerInstance>(null)
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null)
  const prevMiniModeRef = useRef(miniMode)

  const onProviderSetup = (provider: MediaProviderAdapter) => {
    if ((isVideoProvider(provider) || isHLSProvider(provider)) && !videoRef) {
      setVideoRef(provider.video)
    }
  }

  useEffect(() => {
    if (!src) {
      setVideoRef(null)
    } else if (player.current) {
      // clear text tracks when changing videos
      player.current.textTracks.clear()
    }
  }, [src])

  useAnalytics(videoRef, {
    videoId: id,
    playerVersion: appConfig.player.version,
    timeInterval: 20000, // Report time every 20 seconds
    send: async ({ headers, ...metrics }) => {
      delete metrics.__attempts
      if (id && videoRef && !(metrics.action === 'error' && !src)) {
        await reportMutation({
          variables: {
            data: {
              contentId: id,
              ...metrics,
              ...headers,
              isLive: !!liveNow,
              type: remoteType,
              action: metrics.action as ReportAction,
              error: metrics.error && {
                code: metrics.error.code,
                message: metrics.error.message,
                data: JSON.stringify(metrics.error.data),
                source: metrics.error.source?.toString(),
              },
              totalDuration: Math.round(videoRef.duration),
            },
          },
          update: (cache) => {
            if (
              metrics.action === 'time' ||
              metrics.action === 'pause' ||
              metrics.action === 'complete' ||
              metrics.action === 'seek'
            ) {
              cache.updateQuery(
                {
                  query: MediaItemDocument,
                  variables: {
                    data: {
                      id,
                    },
                  },
                },
                (data) => {
                  if (!data?.mediaItem) {
                    return null
                  }
                  return {
                    mediaItem: {
                      ...data.mediaItem,
                      viewer: {
                        __typename: 'Viewer',
                        ...data.mediaItem.viewer,
                        lastViewedAt: new Date(metrics.timestamp),
                        lastPosition: metrics.position,
                        progress: videoRef
                          ? metrics.position / Math.round(videoRef.duration)
                          : data.mediaItem.viewer.progress,
                      },
                    },
                  }
                },
              )
            }
          },
        })
      }
    },
  })

  const playerStyle =
    status !== 'stopped' && src !== null
      ? {
          opacity: 1,
          zIndex: 1000,
          pointerEvents: miniMode ? ('none' as const) : ('auto' as const),
        }
      : {
          opacity: 0,
          zIndex: -1000,
          pointerEvents: 'none' as const,
        }

  const onResize = () => {
    if (window.innerWidth <= 480) {
      if (!mobileRef.current) {
        setMobile(true)
      }
    } else {
      if (mobileRef.current) {
        setMobile(false)
      }
    }
  }

  useEffect(() => {
    mobileRef.current = mobile
  }, [mobile])

  useEffect(() => {
    if (config.mediaBrand) {
      document.documentElement.style.setProperty('--media-brand', config.mediaBrand)
    }
    window.addEventListener('resize', onResize)
    setTimeout(() => {
      try {
        onResize()
      } catch (e) {
        console.error(e)
      }
    }, 50)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  const onCanPlayThrough = async () => {
    if (status === 'playing') {
      if (player.current) {
        const videoPlayer = player.current
        if (
          position &&
          position > 1 &&
          // Update the video player time if it is more than 10 seconds off the currently saved position
          (videoPlayer.currentTime > position + 5 || videoPlayer.currentTime < position - 5)
        ) {
          videoPlayer.currentTime = position
        } else {
          if (position === -1) {
            // When position is set to -1 we are explicitly setting the video back to the beginning
            videoPlayer.currentTime = 0
          }
          videoPlayer
            .play()
            .then(() => {})
            .catch((e) => {
              console.error(e)
            })
        }
      }
    }
  }

  const onProviderChange: (detail: MediaProviderAdapter | null, nativeEvent: MediaProviderChangeEvent) => void = (
    provider,
  ) => {
    if (isHLSProvider(provider)) {
      provider.config = {
        abrEwmaDefaultEstimate: 14670441,
        testBandwidth: false,
      }
    }
  }

  useEffect(() => {
    if (status === 'playing' && player.current) {
      onCanPlayThrough().catch((e) => {
        console.error(e)
      })
    } else if (status === 'stopped') {
      player.current?.pause()
    }
  }, [status])

  useEffect(() => {
    const wasMiniMode = prevMiniModeRef.current
    prevMiniModeRef.current = miniMode

    if (!miniMode && !wasMiniMode) {
      // Genuine route-change scenario: miniMode was already false before this ran.
      dispatch(stopVideo())
    }
  }, [window.location.pathname, miniMode])

  return (
    <div className={'media-player-bounds video-player-container'}>
      <div className={'mini-player-container'}>{children}</div>
      <div className={'media-player-container'} style={playerStyle}>
        <Draggable bounds={'parent'}>
          <div className={'media-player-wrapper'}>
            <MediaPlayer
              key={src}
              onError={(e) => {
                console.error(e)
              }}
              onHlsError={(e) => {
                console.error(e)
              }}
              playsInline
              load={'eager'}
              streamType={liveNow ? 'live:dvr' : 'on-demand'}
              onCanPlayThrough={onCanPlayThrough}
              onProviderChange={onProviderChange}
              ref={player}
              src={src || ''}
              crossOrigin={true}
              onProviderSetup={onProviderSetup}>
              <MediaProvider
                onError={(e) => {
                  console.error(e)
                }}
              />
              <Captions className="vds-captions" />
              <VideoLayout onMiniModeChange={(value) => dispatch(setMiniMode(value))} />
            </MediaPlayer>
          </div>
        </Draggable>
      </div>
    </div>
  )
}

export default VideoPlayer
